import React, { useMemo, useEffect, useState, useCallback } from 'react';
import { Button } from '@fluentui/react-components';
import { CalendarMonthRegular, DismissRegular } from '@fluentui/react-icons';
import { RatingsFilterParams } from '../../../store/features/groundTeamRatings/groundTeamRatingsApi';
import { useRatingsDateFilterStyles } from './RatingsDateFilter.styles';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '../../../store/hooks';
import { selectConfig } from '../../../store/features/config/configSlice';
import {
    selectHelp,
    getConnectToTeamsForReporting,
} from '../../../store/features/help/helpSlice';
import { useTranslation } from 'react-i18next';
import useReportingTeamItems from '../../../hooks/useReportingTeamItems';
import DateRangeFilter from '../../shared/DateRangeFilter/DateRangeFilter';
import { TeamsFilterDropdownProps } from '../../shared/TeamsFilterDropdown/TeamsFilterDropdown';

interface DateFilterProps {
    dateFilter: RatingsFilterParams;
    onDateFilterChange: (filter: RatingsFilterParams) => void;
    onApplyFilter: () => void;
    onClearFilter: () => void;
    showTeamFilter: boolean;
}

const DateFilter: React.FC<DateFilterProps> = ({
    dateFilter,
    onDateFilterChange,
    onApplyFilter,
    onClearFilter,
    showTeamFilter,
}) => {
    const styles = useRatingsDateFilterStyles();
    const config = useSelector(selectConfig);
    const groundTeamRatingsConfig = config?.groundTeamRatingsConfig;
    const { t } = useTranslation();
    const dispatch = useAppDispatch();

    const { reportingTeams, reportingTeamsStatus } = useSelector(selectHelp);
    const { teams, allTeamsOption } = useReportingTeamItems(
        reportingTeams,
        'ConversationRatings_Dropdown_AllTeams'
    );

    useEffect(() => {
        if (
            reportingTeamsStatus === 'loading' ||
            (reportingTeamsStatus === 'idle' && reportingTeams.length === 0)
        ) {
            dispatch(getConnectToTeamsForReporting());
        }
    }, [dispatch, reportingTeamsStatus, reportingTeams.length]);

    const handleTeamChange = useCallback(
        (id: string) => {
            onDateFilterChange({
                ...dateFilter,
                groundTeamIdFilter: id !== allTeamsOption.id ? id : undefined,
            });
        },
        [onDateFilterChange, allTeamsOption, dateFilter]
    );

    const teamFilterDropdownProps: TeamsFilterDropdownProps | undefined =
        useMemo(() => {
            return showTeamFilter
                ? {
                      teams: teams,
                      allTeamsOption: allTeamsOption,
                      selectedTeamId: dateFilter.groundTeamIdFilter || null,
                      onTeamChange: handleTeamChange,
                      teamLabel: 'Ground Team',
                      teamPlaceholder: 'All teams',
                      fieldClassName: styles.dateField,
                  }
                : undefined;
        }, [
            showTeamFilter,
            teams,
            dateFilter.groundTeamIdFilter,
            handleTeamChange,
            allTeamsOption,
            styles.dateField,
        ]);

    // Calculate min/max dates from config
    const { maxDate, maxQueryPeriodDays } = useMemo(() => {
        if (!groundTeamRatingsConfig) {
            return {
                maxDate: new Date(),
                maxQueryPeriodDays: 90,
            };
        }

        return {
            maxDate: new Date(),
            maxQueryPeriodDays: groundTeamRatingsConfig.maxQueryPeriodDays,
        };
    }, [groundTeamRatingsConfig]);

    const [validationValid, setValidationValid] = useState(true);

    const handleFromDateChange = (date: Date | null | undefined) => {
        onDateFilterChange({
            ...dateFilter,
            utcStartDate: date || undefined,
        });
    };

    const handleToDateChange = (date: Date | null | undefined) => {
        onDateFilterChange({
            ...dateFilter,
            utcEndDate: date || undefined,
        });
    };

    const hasActiveFilter =
        dateFilter.utcStartDate ||
        dateFilter.utcEndDate ||
        dateFilter.groundTeamIdFilter;
    const canApply = hasActiveFilter && validationValid;

    return (
        <div className={styles.root}>
            <DateRangeFilter
                startDate={dateFilter.utcStartDate || null}
                endDate={dateFilter.utcEndDate || null}
                onStartDateChange={handleFromDateChange}
                onEndDateChange={handleToDateChange}
                startLabel="From Date"
                endLabel="To Date"
                startPlaceholder="Select start date"
                endPlaceholder="Select end date"
                startMaxDate={dateFilter.utcEndDate || maxDate}
                endMinDate={dateFilter.utcStartDate || null}
                endMaxDate={maxDate}
                fieldClassName={styles.dateField}
                validationConstraints={{
                    maxDaySpan: maxQueryPeriodDays,
                }}
                validationMessages={{
                    startAfterEnd: 'Start date must be before end date',
                    maxDaySpan: `Date range cannot exceed ${maxQueryPeriodDays} days`,
                }}
                onValidationChange={setValidationValid}
                teamFilterDropdownProps={teamFilterDropdownProps}
            />

            <div className={styles.buttonGroup}>
                <Button
                    appearance="primary"
                    icon={<CalendarMonthRegular />}
                    onClick={onApplyFilter}
                    disabled={!canApply}
                >
                    {t('Button_Filter_Apply')}
                </Button>
                {hasActiveFilter && (
                    <Button
                        appearance="secondary"
                        icon={<DismissRegular />}
                        onClick={onClearFilter}
                    >
                        {t('Button_Filter_Clear')}
                    </Button>
                )}
            </div>
        </div>
    );
};

export default DateFilter;
